import React, {useState} from 'react';
import {useTheme} from "@mui/material/styles";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { DaButton } from "../form/form.styles";
import { MessageBox } from "./MessageBox";
import { IMessagePanelProps, MsgBoxType, IUserAutocompleteDialogProps } from "./types";
import {ILookupGroup, ILookupUserSearchTerm, lookupApi} from "../../../_api";
import {useAuth} from "../../../_auth/useAuth";
import {UserGroup, UserGroupDisplay} from "../../../_appData/enums/userGroup";

// Export a mutable object to store selected group name (shared between dialog and parent)
export const selectedGroupNameStore: {groupName: string | undefined} = {groupName: undefined};

export const GroupAddDialog: React.FC<IUserAutocompleteDialogProps> = (
    {
        isOpen = false,
        onClose,
        title = "Choose Authorization Group",
        userId = -1
    }: IUserAutocompleteDialogProps
) => {
    const theme = useTheme();
    const { getAccessToken } = useAuth();
    const [selItem, setSelItem] = React.useState<{group_id: number, group_name: string} | undefined>(undefined);
    const [groupOptions, setGroupOptions] = useState<Array<ILookupGroup>>([]);
    const [isGroupLoading, setIsGroupLoading] = useState<boolean>(false);
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({message: ""});

    const formFieldSize = "medium";
    const formFieldVariant = "standard";

    const handleClose = () => {
        onClose("cancel");
        setSelItem(undefined);
    };

    const handleOk = () => {
        if (selItem) {
            // Store the selected group_name in the shared store for UserAEView to access
            selectedGroupNameStore.groupName = selItem.group_name;
            onClose("ok", 0);
        } else {
            selectedGroupNameStore.groupName = undefined;
            onClose("cancel");
        }
        setSelItem(undefined);
    };

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }

    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const getGroupOptions = async (): Promise<void> => {
        setIsGroupLoading(true);

        const options: ILookupUserSearchTerm = {user_id: userId, search_term: ''};
        const auth_token = await getAccessToken();
        try {
            const resp = await lookupApi.get_user_group_lookup(options, auth_token);
            if (resp) {
                if (resp.success && Array.isArray(resp.data)) {
                    // Set groups exactly as returned by API - group_id may be null, that's fine
                    setGroupOptions(resp.data);
                } else if (resp.error && resp.error.length > 0) {
                    msgBox_Show(
                        "An error occurred while retrieving Authorization Group lookups data. " +
                            "The Authorization Group lookups data was not found.",
                        "Authorization Group lookups Not Found Error",
                        "error"
                    );
                    setGroupOptions([]);
                }
            } else {
                setGroupOptions([]);
            }
        }
        catch (err) {
            msgBox_Show(
                "An error occurred while fetching Authorization Group lookups data. " +
                "There may have been a network error.",
                "Lookup API Fetch Error",
                "error"
            );
            setGroupOptions([]);
        }
        finally {
            setIsGroupLoading(false);
        }
    };

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                fullWidth={true}
                maxWidth={"sm"}
                aria-labelledby="dialog-box-title"
                aria-describedby="dialog-box-description"
            >
                <DialogTitle
                    id="dialog-box-title"
                    sx={{
                        display: "flex",
                        alignItems: "end",
                        justifyContent: "start"
                    }}
                >
                    {title}
                </DialogTitle>
                <DialogContent sx={{whiteSpace: "pre-line"}}>
                    <Autocomplete
                        // value={value}
                        size={formFieldSize}
                        fullWidth
                        // inputValue={inputValue}
                        id=""
                        loading={isGroupLoading}
                        onOpen={getGroupOptions}
                        options={Array.isArray(groupOptions) && groupOptions.length > 0 ? groupOptions.map((group: any) => {
                            // Use group_name from API response directly (format: "ListOwner", "ListAdmin", "Licensee")
                            const group_name = group.group_name || '';
                            const displayName = UserGroupDisplay[group_name as UserGroup] || group_name;
                            // Store group_name - group_id may be null from API, that's fine
                            return {value: {group_id: group.group_id || 0, group_name}, label: displayName};
                        }) : []}
                        getOptionLabel={(option) => option.label}
                        onChange={(e, value) => {
                            setSelItem(value?.value);
                        }}
                        isOptionEqualToValue={(option, value) =>
                            value === undefined || option.value === value.value
                        }
                        noOptionsText={"No options available"}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.value?.group_name || option.label}>
                                {option.label}
                            </Box>
                        )}
                        renderInput={(params) =>
                            <TextField {...params}
                                label="Authorization Group"
                                variant={formFieldVariant}
                                // error={!!errors[name]}
                                // helperText={errors[name]?.message ?? ''}
                            />
                        }
                      />
                </DialogContent>
                <DialogActions>
                    <DaButton buttonStyle="secondary" variant={"outlined"} autoFocus onClick={handleClose}>Cancel</DaButton>
                    <DaButton buttonStyle="primary" variant={"outlined"} onClick={handleOk}>OK</DaButton>
                </DialogActions>
            </Dialog>
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
        </>
    );
}